/**
 * @component ry-button-group
 * @category  form
 * @summary   Visually joins a row (or column) of <ry-button> children — inner borders merge, inter-button margin disappears, radii suppress on internal edges so the row reads as one segmented control.
 *
 * Reference: Fomantic UI's `.ui.buttons`. Every modifier class on `.ui.buttons`
 * has a corresponding attribute here; behavior matches.
 *
 * @attribute vertical    boolean   default=false   Stack vertically instead of horizontally.
 * @attribute fluid       boolean   default=false   Stretch to container width; children share equally.
 * @attribute compact     boolean   default=false   Reduced padding on each child (CSS hook only — children stay the same components).
 * @attribute spaced      boolean   default=false   Un-joined: children keep their margins and rounded corners. Use when grouping buttons logically but not visually.
 * @attribute wrapping    boolean   default=false   Allow children to wrap onto multiple lines. Default is one line, overflowing.
 * @attribute circular    boolean   default=false   All children render as circles. Use with icon-only children.
 * @attribute attached    string?                   top | bottom | left | right — group is attached to an adjacent surface (card, input, panel). Suppresses radius on that edge.
 * @attribute n           number?                   Force equal-width children expressed as N divisions (2…12). Implies fluid.
 * @attribute aria-label  string?                   Required for screen-reader announcement when the group is a logical control. Console warns if missing.
 * @attribute role        string?   default=group   Override to "toolbar" for arrow-key roving-tabindex toolbar patterns.
 *
 * @keyboard Pass-through by default — each child button keeps its own focus + activation. Set role="toolbar" to opt into roving-tabindex arrow-key patterns (consumer-owned).
 *
 * @state    pressed — children manage their own; the group does not coordinate. For radio-style "exactly one pressed", wire it via the parent's ry:click listener.
 *
 * @content  Direct <ry-button> children. Mixing variants, sizes, and palette colors all work. The group only suppresses border-radii on internal edges; visuals are otherwise per-child.
 *
 * @dom
 *   ry-button-group
 *     > ry-button (first — keeps outer-left radius, loses inner-right)
 *     > ry-button (middle — loses both radii)
 *     > ry-button (last — keeps outer-right radius, loses inner-left)
 *
 * @gotcha  Missing aria-label / aria-labelledby logs a warning. A group without a name is invisible to screen readers — it's just three buttons. Always name the logical control.
 *
 * @gotcha  The group does NOT coordinate pressed-state between children. Click-listeners on the parent can implement radio-style behavior, but out of the box pressed states are independent. This is intentional — the group is a layout primitive, not a state controller.
 *
 * @gotcha  attached + spaced together is meaningless. Spaced un-joins children, which defeats the point of attaching the group to an edge. The component does not warn but the result is visually broken.
 *
 * @example horizontal
 * ```html
 * <ry-button-group aria-label="Text alignment">
 *   <ry-button icon aria-label="Align left"><ry-icon name="chevron-left"></ry-icon></ry-button>
 *   <ry-button pressed icon aria-label="Align center"><ry-icon name="menu"></ry-icon></ry-button>
 *   <ry-button icon aria-label="Align right"><ry-icon name="chevron-right"></ry-icon></ry-button>
 * </ry-button-group>
 * ```
 *
 * @example vertical
 * ```html
 * <ry-button-group vertical aria-label="Sort by">
 *   <ry-button>Newest</ry-button>
 *   <ry-button>Oldest</ry-button>
 *   <ry-button>Trending</ry-button>
 * </ry-button-group>
 * ```
 *
 * @example fluid-segmented
 * ```html
 * <ry-button-group fluid aria-label="Plan">
 *   <ry-button>Free</ry-button>
 *   <ry-button variant="primary" pressed>Pro</ry-button>
 *   <ry-button>Enterprise</ry-button>
 * </ry-button-group>
 * ```
 *
 * @example equal-widths-via-n
 * ```html
 * <ry-button-group n="3" aria-label="View">
 *   <ry-button>Day</ry-button>
 *   <ry-button>Week</ry-button>
 *   <ry-button>Month</ry-button>
 * </ry-button-group>
 * ```
 *
 * @example compact
 * ```html
 * <ry-button-group compact aria-label="Format">
 *   <ry-button>B</ry-button>
 *   <ry-button>I</ry-button>
 *   <ry-button>U</ry-button>
 * </ry-button-group>
 * ```
 *
 * @example spaced
 * ```html
 * <ry-button-group spaced aria-label="Actions">
 *   <ry-button>Save</ry-button>
 *   <ry-button variant="basic">Cancel</ry-button>
 * </ry-button-group>
 * ```
 *
 * @example wrapping
 * ```html
 * <ry-button-group wrapping aria-label="Filters">
 *   <ry-button>Apples</ry-button><ry-button>Bananas</ry-button><ry-button>Cherries</ry-button>
 *   <ry-button>Durian</ry-button><ry-button>Elderberry</ry-button><ry-button>Fig</ry-button>
 * </ry-button-group>
 * ```
 *
 * @example circular-icons
 * ```html
 * <ry-button-group circular aria-label="Social">
 *   <ry-button icon aria-label="Like"><ry-icon name="heart"></ry-icon></ry-button>
 *   <ry-button icon aria-label="Star"><ry-icon name="star"></ry-icon></ry-button>
 *   <ry-button icon aria-label="Bookmark"><ry-icon name="bookmark"></ry-icon></ry-button>
 * </ry-button-group>
 * ```
 *
 * @example attached-bottom-of-card
 * ```html
 * <ry-card style="padding: 0">
 *   <div style="padding: 1rem">Your design system. Ship it.</div>
 *   <ry-button-group attached="bottom" fluid aria-label="Card actions">
 *     <ry-button variant="basic">Cancel</ry-button>
 *     <ry-button variant="primary">Accept</ry-button>
 *   </ry-button-group>
 * </ry-card>
 * ```
 *
 * @example colored-mix
 * ```html
 * <ry-button-group aria-label="Quick actions">
 *   <ry-button variant="positive">Yes</ry-button>
 *   <ry-button variant="negative">No</ry-button>
 *   <ry-button>Cancel</ry-button>
 * </ry-button-group>
 * ```
 *
 * @example toolbar-with-arrow-nav
 * ```html
 * <!--
 *   role="toolbar" opts into the WAI-ARIA toolbar pattern. The browser
 *   doesn't implement arrow-key navigation for you — consumers wire it
 *   on the parent. Use when the group is a *toolbar* of icon actions
 *   (text editor, image editor, etc.), not when it's a segmented control.
 * -->
 * <ry-button-group role="toolbar" aria-label="Format toolbar">
 *   <ry-button icon aria-label="Bold"><strong>B</strong></ry-button>
 *   <ry-button icon aria-label="Italic"><em>I</em></ry-button>
 *   <ry-button icon aria-label="Underline"><u>U</u></ry-button>
 * </ry-button-group>
 * ```
 *
 * @tutorial role-defaults-to-group
 * ```js
 * // By default the group announces as a "group" — generic ARIA composite.
 * // For toolbars (icon-only action bars), set role="toolbar" explicitly.
 * const el = mount('<ry-button-group aria-label="Test"><ry-button>1</ry-button></ry-button-group>');
 * await expectAttr(el, 'role', 'group');
 * ```
 *
 * @tutorial respects-explicit-role
 * ```js
 * // Wrong assumption: the group always sets role="group", overriding the
 * // consumer. It doesn't — user-set role wins, so toolbar / radiogroup
 * // patterns are intact.
 * const el = mount('<ry-button-group role="toolbar" aria-label="X"><ry-button>1</ry-button></ry-button-group>');
 * await expectAttr(el, 'role', 'toolbar', 'explicit role survives setup');
 * ```
 *
 * @tutorial warns-without-aria-label
 * ```js
 * // A group without aria-label is just three buttons to a screen reader.
 * // Confirm the warning fires so consumers cannot miss this regression.
 * const warnings = [];
 * const origWarn = console.warn;
 * console.warn = (...args) => { warnings.push(args.join(' ')); };
 * try {
 *   mount('<ry-button-group><ry-button>1</ry-button></ry-button-group>');
 * } finally {
 *   console.warn = origWarn;
 * }
 * await expectEqual(
 *   warnings.some(w => w.includes('aria-label')), true,
 *   'unnamed group emits the warning',
 * );
 *
 * // With aria-label, no warning
 * warnings.length = 0;
 * console.warn = (...args) => { warnings.push(args.join(' ')); };
 * try {
 *   mount('<ry-button-group aria-label="Sort"><ry-button>1</ry-button></ry-button-group>');
 * } finally {
 *   console.warn = origWarn;
 * }
 * await expectEqual(warnings.length, 0, 'aria-label silences the warning');
 *
 * // aria-labelledby also satisfies the requirement (alternative ARIA pattern)
 * warnings.length = 0;
 * console.warn = (...args) => { warnings.push(args.join(' ')); };
 * try {
 *   mount('<div><span id="lbl">Sort</span><ry-button-group aria-labelledby="lbl"><ry-button>1</ry-button></ry-button-group></div>');
 * } finally {
 *   console.warn = origWarn;
 * }
 * await expectEqual(warnings.length, 0, 'aria-labelledby also silences');
 * ```
 *
 * @tutorial click-passes-through
 * ```js
 * // Wrong assumption: the group intercepts child clicks (the way a radio
 * // group intercepts radio-button changes). It does NOT — children fire
 * // their own ry:click events normally. Composition is one-directional.
 * const root = mount(`
 *   <ry-button-group aria-label="Actions">
 *     <ry-button id="a">A</ry-button>
 *     <ry-button id="b">B</ry-button>
 *   </ry-button-group>
 * `);
 * const a = root.querySelector('#a');
 * await expectEvent(a, 'ry:click', () => a.click());
 * ```
 *
 * @tutorial does-not-coordinate-pressed
 * ```js
 * // Wrong assumption (radio-group pattern): pressing one button un-presses
 * // the others. The group does NOT do that. Pressed state is independent
 * // per child by design. Consumers who want radio behavior wire it themselves
 * // (e.g. a ry:click listener on the group that clears siblings).
 * const root = mount(`
 *   <ry-button-group aria-label="View">
 *     <ry-button id="a" pressed>Day</ry-button>
 *     <ry-button id="b">Week</ry-button>
 *   </ry-button-group>
 * `);
 * const a = root.querySelector('#a');
 * const b = root.querySelector('#b');
 * b.click();
 * await expectEqual(a.pressed, true, 'a remains pressed — group does not auto-clear');
 * await expectEqual(b.pressed, false, 'b is not auto-pressed by click — pressed is sticky-by-attribute');
 * ```
 *
 * @chain ry-button
 * ```js
 * // Composition proof: ry-button children inside a group keep their full
 * // ARIA + keyboard contract. The group is purely visual layout.
 * const root = mount(`
 *   <ry-button-group aria-label="Test">
 *     <ry-button>A</ry-button>
 *     <ry-button disabled>B</ry-button>
 *     <ry-button loading>C</ry-button>
 *   </ry-button-group>
 * `);
 * const [a, b, c] = root.querySelectorAll('ry-button');
 *
 * // First child — fully interactive
 * await expectAttr(a, 'role', 'button');
 * await expectAttr(a, 'tabindex', '0');
 * await expectEvent(a, 'ry:click', () => a.click());
 *
 * // Disabled child — ARIA-disabled, untabbable, click suppressed
 * await expectAttr(b, 'aria-disabled', 'true');
 * await expectAttr(b, 'tabindex', '-1');
 *
 * // Loading child — ARIA-busy, untabbable
 * await expectAttr(c, 'aria-busy', 'true');
 * await expectAttr(c, 'tabindex', '-1');
 * ```
 *
 * @see ry-button
 * @see ry-toggle-button
 */

import { RyElement } from '../core/ry-element.js';

export class RyButtonGroup extends RyElement {
  static get observedAttributes(): string[] {
    return ['n'];
  }

  setup(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'group');
    this.#warnIfUnnamed();
    this.#applyEqualWidths();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;
    if (name === 'n') this.#applyEqualWidths();
  }

  #warnIfUnnamed(): void {
    if (this.hasAttribute('aria-label') || this.hasAttribute('aria-labelledby')) return;
    // eslint-disable-next-line no-console
    console.warn(
      '[ry-button-group] missing aria-label / aria-labelledby — screen readers will not announce the group',
      this
    );
  }

  /** When [n] is set, behave as if [fluid] and give each child 1/n width via
      a custom property the structural CSS reads. */
  #applyEqualWidths(): void {
    const n = parseInt(this.getAttribute('n') ?? '', 10);
    if (Number.isFinite(n) && n >= 2 && n <= 12) {
      this.style.setProperty('--ry-buttons-n', String(n));
    } else {
      this.style.removeProperty('--ry-buttons-n');
    }
  }
}

customElements.define('ry-button-group', RyButtonGroup);
